import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Link,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import approvedBtnTick from "../../Photos/Newuiphotos/VerificationCenter/approvedBtnTick.webp";
import rejectedBtnCross from "../../Photos/Newuiphotos/VerificationCenter/rejectedBtnCross.webp";
import backAngleBracketIcon from "../../Photos/Newuiphotos/VerificationCenter/backAngleBracketIcon.webp";
import toast from "react-hot-toast";
import moment from "moment";
import {
  isApprovedDonationStatus,
  isSafeHttpUrl,
  openSafePaymentLink,
} from "../../helpers/donationPaymentLink";
import PaymentLinkWarningDialog from "./PaymentLinkWarningDialog";
import DonationEntityIdLink from "./DonationEntityIdLink";

export type DonationLinkReviewItem = {
  _id: string;
  entityId: string;
  entityType: string;
  entityName?: string | null;
  title: string;
  description?: string | null;
  status: string;
  isCustomLink: boolean;
  donationType?: string | null;
  customPaymentLink?: string | null;
  isVerified?: boolean;
  verifiedBy?: string | null;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  publishedAt?: string | null;
};

interface DonationLinkReviewModalProps {
  open: boolean;
  onClose: () => void;
  donation: DonationLinkReviewItem | null;
  onConfirmAction: (action: "approved" | "rejected", reason: string) => void;
  isLoading: boolean;
}

const DonationLinkReviewModal = ({
  open,
  onClose,
  donation,
  onConfirmAction,
  isLoading,
}: DonationLinkReviewModalProps) => {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const [showReasonConfirmation, setShowReasonConfirmation] = useState(false);
  const [changingStatusTo, setChangingStatusTo] = useState<
    "approve" | "reject" | ""
  >("");
  const [linkWarningOpen, setLinkWarningOpen] = useState(false);

  const isPending = donation?.status === "pending" && !donation?.isVerified;

  const resetLocalState = () => {
    setReason("");
    setReasonError(false);
    setShowReasonConfirmation(false);
    setChangingStatusTo("");
    setLinkWarningOpen(false);
  };

  useEffect(() => {
    if (open) return;
    setReason("");
    setReasonError(false);
    setShowReasonConfirmation(false);
    setChangingStatusTo("");
    setLinkWarningOpen(false);
  }, [open]);

  const resetAndClose = () => {
    if (isLoading) return;
    resetLocalState();
    onClose();
  };

  const handleApproveReject = (status: "approve" | "reject") => {
    setChangingStatusTo(status);
    setShowReasonConfirmation(true);
  };

  const handleConfirm = () => {
    if (changingStatusTo === "reject" && !reason.trim()) {
      toast.error("Rejection reason is required!");
      setReasonError(true);
      return;
    }
    const action = changingStatusTo === "approve" ? "approved" : "rejected";
    onConfirmAction(action, reason.trim());
  };

  const handlePaymentLinkClick = () => {
    if (!donation?.customPaymentLink) return;
    if (!isSafeHttpUrl(donation.customPaymentLink)) {
      toast.error(
        "This payment link is not a valid http(s) URL and cannot be opened."
      );
      return;
    }
    if (isApprovedDonationStatus(donation.status)) {
      openSafePaymentLink(donation.customPaymentLink);
      return;
    }
    setLinkWarningOpen(true);
  };

  if (!donation) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (isLoading) return;
          if (showReasonConfirmation) {
            setShowReasonConfirmation(false);
            return;
          }
          resetAndClose();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "22px" } }}
      >
        <DialogTitle>
          {showReasonConfirmation
            ? changingStatusTo === "approve"
              ? "Confirm Approval"
              : "Reason for Rejection"
            : "Donation Link Details"}
          <IconButton
            aria-label="close"
            onClick={() => {
              if (isLoading) return;
              if (showReasonConfirmation) {
                setShowReasonConfirmation(false);
                return;
              }
              resetAndClose();
            }}
            disabled={isLoading}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            overflowX: "hidden",
            gap: 1.5,
            pb: 3,
          }}
        >
          {showReasonConfirmation ? (
            <Box>
              {changingStatusTo === "reject" ? (
                <TextField
                  fullWidth
                  label="Reason for Rejecting"
                  sx={{ border: reasonError ? "1px solid red" : "", mt: 1 }}
                  value={reason}
                  onChange={(e) => {
                    setReasonError(false);
                    setReason(e.target.value);
                  }}
                  multiline
                  rows={4}
                  required
                  disabled={isLoading}
                />
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Approve this custom payment link and publish the campaign for
                  donors?
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                  gap: 3,
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    width: "180px",
                    borderRadius: "12px",
                    bgcolor: "#B0B0B0",
                    color: "white",
                    "&:hover": { bgcolor: "#B0B0B0" },
                    fontSize: "13px",
                    textTransform: "none",
                  }}
                  onClick={() => setShowReasonConfirmation(false)}
                  disabled={isLoading}
                >
                  <Box
                    component="img"
                    src={backAngleBracketIcon}
                    width="9px"
                    marginRight="5px"
                  />
                  Back
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    width: "180px",
                    borderRadius: "12px",
                    bgcolor:
                      changingStatusTo === "approve" ? "#00D23B" : "#F93737",
                    "&:hover": {
                      bgcolor:
                        changingStatusTo === "approve" ? "#00D23B" : "#F93737",
                    },
                    "&.Mui-disabled": { bgcolor: "#B0B0B0", color: "white" },
                    fontSize: "13px",
                    textTransform: "none",
                  }}
                  onClick={handleConfirm}
                  disabled={isLoading}
                >
                  <Box
                    component="img"
                    src={
                      changingStatusTo === "approve"
                        ? approvedBtnTick
                        : rejectedBtnCross
                    }
                    width="11px"
                    marginRight="5px"
                  />
                  {changingStatusTo === "approve"
                    ? "Confirm Approve"
                    : "Confirm Reject"}
                  {isLoading && (
                    <CircularProgress sx={{ ml: 1 }} size={18} color="inherit" />
                  )}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <DetailRow label="Title" value={donation.title} />
              <DetailRow
                label="Entity Type"
                value={donation.entityType || "—"}
              />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Entity Name
                </Typography>
                {donation.entityName ? (
                  <DonationEntityIdLink
                    entityId={donation.entityId}
                    entityType={donation.entityType}
                    label={donation.entityName}
                  />
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Entity ID
                </Typography>
                <DonationEntityIdLink
                  entityId={donation.entityId}
                  entityType={donation.entityType}
                />
              </Box>
              <DetailRow label="Status" value={donation.status} capitalize />
              <DetailRow
                label="Verified"
                value={donation.isVerified ? "Yes" : "No"}
              />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Custom Payment Link
                </Typography>
                {donation.customPaymentLink ? (
                  isSafeHttpUrl(donation.customPaymentLink) ? (
                    <Link
                      component="button"
                      type="button"
                      onClick={handlePaymentLinkClick}
                      sx={{
                        wordBreak: "break-all",
                        fontSize: 14,
                        textAlign: "left",
                      }}
                    >
                      {donation.customPaymentLink}
                    </Link>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: "break-all" }}
                    >
                      {donation.customPaymentLink}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
              {donation.description && (
                <DetailRow label="Description" value={donation.description} />
              )}
              {donation.status === "rejected" && donation.rejectionReason && (
                <DetailRow
                  label="Rejection Reason"
                  value={donation.rejectionReason}
                />
              )}
              <DetailRow
                label="Created At"
                value={moment
                  .utc(donation.createdAt)
                  .local()
                  .format("DD-MMM-YYYY HH:mm:ss")}
              />

              {isPending && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 3,
                    mt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      width: "160px",
                      borderRadius: "12px",
                      bgcolor: "#00D23B",
                      "&:hover": { bgcolor: "#00D23B" },
                      textTransform: "none",
                    }}
                    onClick={() => handleApproveReject("approve")}
                  >
                    <Box
                      component="img"
                      src={approvedBtnTick}
                      width="14px"
                      marginRight="5px"
                    />
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      width: "160px",
                      borderRadius: "12px",
                      bgcolor: "#F93737",
                      "&:hover": { bgcolor: "#F93737" },
                      textTransform: "none",
                    }}
                    onClick={() => handleApproveReject("reject")}
                  >
                    <Box
                      component="img"
                      src={rejectedBtnCross}
                      width="11px"
                      marginRight="5px"
                    />
                    Reject
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <PaymentLinkWarningDialog
        open={linkWarningOpen}
        link={donation.customPaymentLink}
        status={donation.status}
        onClose={() => setLinkWarningOpen(false)}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
  capitalize,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
}) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ textTransform: capitalize ? "capitalize" : "none" }}
    >
      {value}
    </Typography>
  </Box>
);

export default DonationLinkReviewModal;
